import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { fetchOrgLocations } from "@/lib/locations/fetchOrgLocations";
import { supabase } from "@/lib/supabase";
import { salesDeskLeadFromRow, type SalesDeskLead } from "./salesDeskLead";
import type { SalesDeskLocationOption } from "./salesDeskStockRequest";

export function useSalesDeskHome(): {
  locationLabel: string;
  locations: SalesDeskLocationOption[];
  activeLocationId: string | null;
  leads: SalesDeskLead[];
  leadsLoading: boolean;
} {
  const orgId = useScopedOrganizationId();
  const auth = useAuth();
  const [locationLabel, setLocationLabel] = useState("");
  const [locations, setLocations] = useState<SalesDeskLocationOption[]>([]);
  const [leads, setLeads] = useState<SalesDeskLead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(true);

  useEffect(() => {
    if (!orgId || !supabase) {
      setLeadsLoading(false);
      return;
    }
    const db = supabase;
    let cancelled = false;
    (async () => {
      const [locationRes, leadRes] = await Promise.all([
        fetchOrgLocations(db, orgId),
        db
          .from("leads")
          .select("id, data")
          .eq("organization_id", orgId)
          .in("status", ["draft", "sent"])
          .order("updated_at", { ascending: false })
          .limit(8),
      ]);
      if (cancelled) return;
      const options = locationRes.data.map((location) => ({
        id: location.id,
        label: location.city?.trim() || location.name.trim(),
      }));
      const active = options.find((location) => location.id === auth.activeLocationId) ?? options[0];
      setLocations(options);
      setLocationLabel(active?.label ?? "");
      const rows = (leadRes.data ?? [])
        .map((row) => salesDeskLeadFromRow({ id: String(row.id), data: row.data }))
        .filter((lead): lead is SalesDeskLead => Boolean(lead))
        .slice(0, 3);
      setLeads(rows);
      setLeadsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, auth.activeLocationId]);

  return { locationLabel, locations, activeLocationId: auth.activeLocationId ?? null, leads, leadsLoading };
}
